import React, { useEffect, useState } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import StatsCards from '@/components/StatsCards';
import DailyChart from '@/components/DailyChart';
import ASNStatistics from '@/components/ASNStatistics';
import CountryStatistics from '@/components/CountryStatistics';
import NodeMap from '@/components/NodeMap';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import ToolsPage from '@/pages/ToolsPage';
import IPListPage from '@/pages/IPListPage';
import SetupPage from '@/pages/SetupPage';
import FeaturesPage from '@/pages/FeaturesPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import ASVerifyPage from '@/pages/ASVerifyPage';
import UnsubscribePage from '@/pages/UnsubscribePage';
import NotFoundPage from '@/pages/NotFoundPage';
import StatsPage from '@/pages/StatsPage';
import ThreatMapPage from '@/pages/ThreatMapPage';
import CVEPage from '@/pages/CVEPage';
import SponsorsPage from '@/pages/SponsorsPage';
import FeedbackWidget from '@/components/FeedbackWidget';
import ScrollToTop from '@/components/ScrollToTop';
import { AlertCircle, RefreshCw } from 'lucide-react';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IS_PRERENDER } from '@/lib/prerender';

const RTBH_API_URL = 'https://api.threat.live/api/get_dashboard_stats';
const RTBH_NODES_URL = 'https://api.threat.live/get_nodes';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [nodesData, setNodesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = async () => {
    if (IS_PRERENDER) return;
    setIsLoading(true);
    setError(null);
    try {
      // Fetch both dashboard stats and nodes data
      const [statsResponse, nodesResponse] = await Promise.all([
        fetch(RTBH_API_URL),
        fetch(RTBH_NODES_URL)
      ]);
      
      if (!statsResponse.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const statsResult = await statsResponse.json();
      if (statsResult.success) {
        setData(statsResult.data);
        setLastUpdated(statsResult.timestamp);
      } else {
        throw new Error('API returned unsuccessful response');
      }

      // Process nodes data - response is direct array
      if (nodesResponse.ok) {
        const nodes = await nodesResponse.json();
        if (Array.isArray(nodes) && nodes.length > 0) {
          const uniqueCountries = [...new Set(nodes.map(node => node.country).filter(Boolean))];
          setNodesData({ 
            nodes: nodes, 
            uniqueCountryCount: uniqueCountries.length 
          });
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching RTBH data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <SEO page="home" />
      <Header />
      
      {/* Hero Section - Full Width */}
      <HeroSection data={data} nodesData={nodesData} />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="mb-8" data-testid="error-alert">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <section className="mb-12 mt-8">
          <StatsCards data={data} isLoading={isLoading} />
        </section>

        {/* Daily Chart */}
        <section className="mb-12">
          <DailyChart data={data} isLoading={isLoading} />
        </section>

        {/* ASN & Country Statistics */}
        <section className="grid lg:grid-cols-2 gap-8 mb-12">
          <ASNStatistics data={data} isLoading={isLoading} />
          <CountryStatistics data={data} isLoading={isLoading} />
        </section>

        {/* Node Map */}
        <section className="mb-12">
          <NodeMap />
        </section>

        {/* About Section */}
        <AboutSection />
      </main>

      <Footer />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/ip-list" element={<IPListPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/map" element={<ThreatMapPage />} />
            <Route path="/cve" element={<CVEPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/as/verify" element={<ASVerifyPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <FeedbackWidgetWrapper />
        </BrowserRouter>
      </LanguageProvider>
    </ThemeProvider>
  );
}

// Hide FeedbackWidget on /map page
const FeedbackWidgetWrapper = () => {
  const location = useLocation();
  if (location.pathname === '/map') return null;
  return <FeedbackWidget />;
};

export default App;
